/**
 * Formatea una fecha/hora para mostrar en la UI.
 * El backend envía fechas en America/Mexico_City (mx_datetime). Si dateStr es
 * "Y-m-d H:i:s" sin zona, el navegador lo interpreta como hora local.
 * Formato salida: DD/MM/YYYY HH:mm (24h).
 *
 * @param {string|null|undefined} dateStr - ISO o "Y-m-d H:i:s" desde el API
 * @returns {string} "DD/MM/YYYY HH:mm" o "-" si no hay fecha
 */
export function formatDateTimeLocal(dateStr) {
  if (dateStr == null || dateStr === '') return '-';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
}

/**
 * Misma lógica pero solo fecha DD/MM/YYYY.
 */
export function formatDateLocal(dateStr) {
  if (dateStr == null || dateStr === '') return '-';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
}

/**
 * Formatea una fecha/hora UTC (ISO) a hora México (America/Mexico_City).
 * Formato salida: DD/MM/YYYY HH:mm (24h).
 */
export function formatDateTimeMexico(dateStr) {
  if (dateStr == null || dateStr === '') return '-';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '-';
    const formatter = new Intl.DateTimeFormat('es-MX', {
      timeZone: 'America/Mexico_City',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return formatter.format(date);
  } catch {
    return '-';
  }
}

/**
 * La fecha de hoy en México, `YYYY-MM-DD`.
 *
 * `new Date().toISOString()` da la fecha en UTC, y a partir de las 6 de la
 * tarde hora de México eso ya es el día siguiente: el tablero pedía al servidor
 * un día que todavía no había empezado y contestaba cero. Tampoco sirve el
 * reloj local del navegador, porque un administrador fuera del país vería otro
 * corte que el que vive la tienda.
 */
export function hoyEnMexico() {
  // `en-CA` es el locale que formatea como YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Mexico_City',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Resta días, meses o años a una fecha `YYYY-MM-DD` y devuelve otra igual.
 *
 * La cuenta se hace en UTC a propósito: son fechas de calendario, sin hora, y
 * usar los setters locales metería el desfase del navegador en un dato que no
 * lo tiene.
 */
export function fechaMenos(fechaISO, { dias = 0, meses = 0, anios = 0 } = {}) {
  const d = new Date(`${fechaISO}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return fechaISO;
  if (anios) d.setUTCFullYear(d.getUTCFullYear() - anios);
  if (meses) d.setUTCMonth(d.getUTCMonth() - meses);
  if (dias) d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().split('T')[0];
}
